// Reunderwriting Module (Tier 3) PR 4 — Quality metrics section.
// DQR distribution + failures-by-lane + failures-by-tier + top-failure-types.

import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/design-system/components/ui";
import { CATEGORICAL, chartTheme } from "@/pages/dealFlow/metrics/chartTheme";
import { useReunderwritingQualityMetrics } from "@/hooks/reunderwriting/useReunderwriting";
import type {
  ReunderwritingFailureTypeBucket,
  ReunderwritingLaneFailureBucket,
  ReunderwritingMetricsFilter,
} from "@/types/reunderwriting";

interface Props {
  filter: ReunderwritingMetricsFilter;
}

export function QualitySection({ filter }: Props) {
  const navigate = useNavigate();
  const q = useReunderwritingQualityMetrics(filter);

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState />;
  const { dqr, failuresByLane, failuresByTier, topFailureTypes } = q.data;

  return (
    <div
      className="grid grid-cols-1 gap-space-4 lg:grid-cols-2"
      data-testid="reunderwriting-metrics-quality"
    >
      <Card>
        <CardHeader>
          <CardTitle>DQR distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {dqr.every((b) => b.caseCount === 0) ? (
            <EmptyState label="No finalized outcomes in this window." />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dqr} margin={{ left: 8, right: 8 }}>
                <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke={chartTheme.axis} fontSize={11} />
                <YAxis stroke={chartTheme.axis} />
                <Tooltip />
                <Bar dataKey="caseCount">
                  {dqr.map((d, i) => (
                    <Cell
                      key={d.dqrValue}
                      fill={CATEGORICAL[i % CATEGORICAL.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Failures by tier</CardTitle>
        </CardHeader>
        <CardContent>
          {failuresByTier.length === 0 ? (
            <EmptyState label="No failures in this window." />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={failuresByTier}
                  dataKey="failureCount"
                  nameKey="label"
                  innerRadius={50}
                  outerRadius={100}
                  paddingAngle={2}
                >
                  {failuresByTier.map((d, i) => (
                    <Cell key={d.tier} fill={CATEGORICAL[i % CATEGORICAL.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Failures by lane</CardTitle>
        </CardHeader>
        <CardContent>
          {failuresByLane.length === 0 ? (
            <EmptyState label="No failures in this window." />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={failuresByLane}
                  dataKey="failureCount"
                  nameKey="label"
                  innerRadius={50}
                  outerRadius={100}
                  paddingAngle={2}
                  onClick={(slice) => {
                    const lane = (slice as { payload: ReunderwritingLaneFailureBucket })
                      .payload?.lane;
                    if (lane) navigate(`/reunderwriting/failure-tracking?lane=${lane}`);
                  }}
                >
                  {failuresByLane.map((d, i) => (
                    <Cell
                      key={d.lane}
                      fill={CATEGORICAL[i % CATEGORICAL.length]}
                      cursor="pointer"
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Top failure types</CardTitle>
        </CardHeader>
        <CardContent>
          {topFailureTypes.length === 0 ? (
            <EmptyState label="No item-level failures in this window." />
          ) : (
            <table className="w-full text-small" aria-label="Top failure types">
              <thead className="text-caption text-gray-mid text-left">
                <tr>
                  <th className="py-space-1">Code</th>
                  <th>Title</th>
                  <th className="text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {topFailureTypes.map((row: ReunderwritingFailureTypeBucket) => (
                  <tr key={row.catalogCode} className="border-t border-border-light">
                    <td className="py-space-2 font-mono">{row.catalogCode}</td>
                    <td className="py-space-2">{row.catalogTitle}</td>
                    <td className="py-space-2 text-right">{row.failureCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-gray-mid">
      Loading quality metrics…
    </div>
  );
}

function ErrorState() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-status-error">
      Failed to load quality metrics.
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-[260px] items-center justify-center text-small text-gray-mid">
      {label}
    </div>
  );
}
